import { type FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { changePassword, ApiError } from '../../lib/api';
import { useAuth } from './AuthProvider';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import BrandMark from '../../components/ui/BrandMark';
import cardStyles from './LoginPage.module.css';
import styles from './ChangePasswordPage.module.css';

const MIN_LENGTH = 12;

/**
 * ChangePasswordPage — blocking screen shown by RequireAuth whenever
 * user.mustChangePassword is true (seeded staff share the default password
 * ChangeMe!2026). Renders instead of AppShell/nav until the password is
 * changed; no app route is reachable until this resolves.
 */
export default function ChangePasswordPage() {
  const { signOut, clearSession } = useAuth();
  const navigate = useNavigate();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fieldError, setFieldError] = useState<string | undefined>();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitError(null);
    setFieldError(undefined);

    if (newPassword.length < MIN_LENGTH) {
      setFieldError(`New password must be at least ${MIN_LENGTH} characters.`);
      return;
    }
    if (newPassword === currentPassword) {
      setFieldError('New password must be different from your current password.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setFieldError('New password and confirmation do not match.');
      return;
    }

    setSubmitting(true);
    try {
      // 204 No Content — the backend revokes every refresh token for this
      // user and clears the refresh cookie. The access token we're still
      // holding also embeds a stale mustChangePassword:true claim until the
      // next login, so we can't just keep going on this session: clear it
      // locally and send the user back through /login for a fresh token.
      await changePassword({ currentPassword, newPassword });
      clearSession();
      navigate('/login', { replace: true, state: { passwordChanged: true } });
    } catch (err: unknown) {
      const msg = err instanceof ApiError ? err.message : 'Unable to update password. Please try again.';
      setSubmitError(
        err instanceof ApiError && err.code === 'INVALID_CURRENT_PASSWORD' ? 'Current password is incorrect.' : msg,
      );
      setSubmitting(false);
    }
  }

  return (
    <div className={styles.page}>
      <div className={cardStyles.card}>
        <div className={styles.logo}>
          <div className={styles.logoMark} aria-hidden="true">
            <BrandMark color="oklch(0.96 0.010 220)" size={20} />
          </div>
          <span className={styles.logoText}>Onboarding</span>
        </div>

        <h1 className={cardStyles.cardTitle}>Update your password</h1>
        <p className={cardStyles.cardSubtitle} id="change-password-desc">
          Your account still uses the default password. Set a new password to continue to the portal.
        </p>

        <form
          className={cardStyles.form}
          onSubmit={(e) => void handleSubmit(e)}
          aria-describedby={submitError ? 'change-password-error' : 'change-password-desc'}
          noValidate
        >
          {submitError && (
            <div id="change-password-error" role="alert" aria-live="assertive" className={cardStyles.errorBanner}>
              {submitError}
            </div>
          )}

          <Input
            label="Current password"
            id="current-password"
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            autoComplete="current-password"
            required
            disabled={submitting}
          />

          <Input
            label="New password"
            id="new-password"
            type="password"
            value={newPassword}
            onChange={(e) => {
              setNewPassword(e.target.value);
              setFieldError(undefined);
            }}
            placeholder={`At least ${MIN_LENGTH} characters`}
            autoComplete="new-password"
            minLength={MIN_LENGTH}
            required
            disabled={submitting}
            error={fieldError}
          />

          <Input
            label="Confirm new password"
            id="confirm-password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            minLength={MIN_LENGTH}
            required
            disabled={submitting}
          />

          <Button
            type="submit"
            className={cardStyles.submitBtn}
            disabled={!currentPassword || !newPassword || !confirmPassword}
            loading={submitting}
          >
            {submitting ? 'Updating…' : 'Update password'}
          </Button>

          <Button
            type="button"
            variant="ghost"
            className={styles.signOutBtn}
            onClick={() => void signOut()}
            disabled={submitting}
          >
            Sign out instead
          </Button>
        </form>
      </div>
    </div>
  );
}
